import { useCallback, useEffect, useRef, useState } from 'react'
import { getFadeMask, getScrollEdges, getScrollLeftToReveal, type ScrollEdges } from './scrollEdges'

export interface ScrollFadeOptions {
  /** Включает слежение; выключенная полоса не крутится и не гаснет. По умолчанию включено. */
  enabled?: boolean
  /** Селектор элемента, который должен быть в видимой части полосы (активная вкладка, текущий этап). */
  revealSelector?: string
  /** Меняется, когда нужно подкрутить заново (смена значения вкладки, статусов этапов). */
  revealKey?: string | number
  /** Меняется, когда у полосы меняется состав детей — их размеры нужно отслеживать заново. */
  childrenKey?: string | number
}

/**
 * Обвязка горизонтально прокручиваемой полосы с затуханием у края, за которым есть скрытое.
 * Края считаются по прокрутке и по размеру полосы и её детей; элемент `revealSelector`
 * подкручивается в видимую часть, если виден не целиком (виден целиком — полоса не дёргается).
 * Вешается на полосу: `ref`, `onScroll`, `style`.
 */
export function useScrollFade<T extends HTMLElement>({
  enabled = true,
  revealSelector,
  revealKey,
  childrenKey,
}: ScrollFadeOptions = {}) {
  const ref = useRef<T>(null)
  const [edges, setEdges] = useState<ScrollEdges>({ start: false, end: false })

  const updateEdges = useCallback(() => {
    const el = ref.current
    if (!el) return
    const next = getScrollEdges(el.scrollLeft, el.clientWidth, el.scrollWidth)
    setEdges((prev) => (prev.start === next.start && prev.end === next.end ? prev : next))
  }, [])

  const reveal = useCallback(() => {
    const el = ref.current
    const target = revealSelector ? el?.querySelector<HTMLElement>(revealSelector) : null
    if (!el || !target) return
    const listRect = el.getBoundingClientRect()
    const itemRect = target.getBoundingClientRect()
    const next = getScrollLeftToReveal(
      el.scrollLeft,
      el.clientWidth,
      itemRect.left - listRect.left + el.scrollLeft,
      itemRect.width
    )
    if (next !== el.scrollLeft) el.scrollLeft = next
  }, [revealSelector])

  useEffect(() => {
    const el = ref.current
    if (!enabled || !el) return
    reveal()
    updateEdges()
    // Размер полосы не меняется, когда меняются подписи или бейджи, — следим и за детьми.
    const observer = new ResizeObserver(() => {
      reveal()
      updateEdges()
    })
    observer.observe(el)
    Array.from(el.children).forEach((child) => observer.observe(child))
    return () => observer.disconnect()
  }, [enabled, reveal, updateEdges, childrenKey])

  useEffect(() => {
    if (enabled) reveal()
  }, [enabled, revealKey, reveal])

  const mask = enabled ? getFadeMask(edges) : undefined

  return {
    ref,
    onScroll: enabled ? updateEdges : undefined,
    style: mask ? { maskImage: mask, WebkitMaskImage: mask } : undefined,
  }
}
